import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import {
  useSchemas,
  useTables,
  useTableDetail,
  useAddEdge,
} from '../../../hooks/useDbDictionary.js';
import type { AddEdgeInput } from '../../../types/db-dictionary.js';

interface AddEdgePopupProps {
  /** 編集対象テーブル */
  parentTable: { schema: string; table: string };
  /** カラム指定で開いた場合、source の column を固定 */
  sourceColumn?: string;
  /** popup を閉じるコールバック */
  onClose: () => void;
  /** 追加するエッジ種別 (REFERENCES / SAME_ENTITY / USES_ENUM)。指定なしは UI で選択 */
  defaultEdgeType?: 'REFERENCES' | 'SAME_ENTITY' | 'USES_ENUM';
}

/**
 * エッジ追加ポップアップ (DB → Table → Column 選択)
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business エッジ追加 popup。FK / SAME_ENTITY / USES_ENUM を edgeType で切り替え、対象を DB→Table→Column で選択して POST /edges を呼ぶ
 * @graph-connects useSchemas [calls] スキーマ一覧
 * @graph-connects useTables [calls] テーブル一覧
 * @graph-connects useTableDetail [calls] カラム一覧
 * @graph-connects useAddEdge [calls] エッジ追加 mutation
 */
export function AddEdgePopup({
  parentTable,
  sourceColumn,
  onClose,
  defaultEdgeType = 'REFERENCES',
}: AddEdgePopupProps) {
  const [edgeType, setEdgeType] = useState<'REFERENCES' | 'SAME_ENTITY' | 'USES_ENUM'>(
    defaultEdgeType,
  );
  const [srcCol, setSrcCol] = useState<string>(sourceColumn ?? '');
  const [tgtSchema, setTgtSchema] = useState<string>('');
  const [tgtTable, setTgtTable] = useState<string>('');
  const [tgtCol, setTgtCol] = useState<string>('id');
  const [note, setNote] = useState<string>('');
  const [enumName, setEnumName] = useState<string>('');
  const [nodeType, setNodeType] = useState<'Table' | 'Column'>(sourceColumn ? 'Column' : 'Table');

  const schemas = useSchemas();
  const tables = useTables(tgtSchema);
  const tgtDetail = useTableDetail(tgtSchema, tgtTable);
  // ソース列選択用にこのテーブル自身の詳細
  const srcDetail = useTableDetail(parentTable.schema, parentTable.table);

  const addEdge = useAddEdge(parentTable.schema, parentTable.table);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  const parentQN = `${parentTable.schema}.${parentTable.table}`;
  const tgtQN = tgtSchema && tgtTable ? `${tgtSchema}.${tgtTable}` : '';

  const handleSubmit = () => {
    let input: AddEdgeInput;
    if (edgeType === 'REFERENCES') {
      if (!srcCol || !tgtQN || !tgtCol) return;
      input = {
        edgeType: 'REFERENCES',
        srcTable: parentQN,
        srcCol,
        tgtTable: tgtQN,
        tgtCol,
      };
    } else if (edgeType === 'SAME_ENTITY') {
      if (!tgtQN) return;
      const node1QN = nodeType === 'Column' && srcCol ? `${parentQN}.${srcCol}` : parentQN;
      const node2QN = nodeType === 'Column' && tgtCol ? `${tgtQN}.${tgtCol}` : tgtQN;
      input = {
        edgeType: 'SAME_ENTITY',
        nodeType,
        node1QN,
        node2QN,
        note: note || undefined,
      };
    } else {
      if (!srcCol || !enumName) return;
      input = {
        edgeType: 'USES_ENUM',
        columnQN: `${parentQN}.${srcCol}`,
        enumName,
      };
    }
    addEdge.mutate(input, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  const sourceColumns = srcDetail.data?.columns ?? [];
  const targetColumns = tgtDetail.data?.columns ?? [];

  return createPortal(
    <div
      className="fixed inset-0 z-[calc(var(--z-modal)+1)] flex items-start justify-center pt-[4.5rem] pb-8"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-2xl mx-4 max-h-[calc(100vh-4rem)] overflow-y-auto bg-[var(--color-bg-surface)] rounded-xl border border-[var(--color-border)] shadow-2xl animate-fade-in"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="sticky top-0 z-10 bg-[var(--color-bg-surface)] border-b border-[var(--color-border)] px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-[var(--color-text-primary)]">エッジ追加</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5 text-[var(--color-text-muted)]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* edgeType 切替 */}
          <div>
            <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
              エッジ種別
            </label>
            <div className="flex gap-2">
              {(['REFERENCES', 'SAME_ENTITY', 'USES_ENUM'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => {
                    setEdgeType(t);
                  }}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                    edgeType === t
                      ? 'border-[var(--color-accent-primary)] bg-[var(--color-accent-primary)]/10 text-[var(--color-accent-primary)]'
                      : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-hover)]'
                  }`}
                >
                  {t === 'REFERENCES' ? 'FK' : t === 'SAME_ENTITY' ? 'SAME_ENTITY' : 'USES_ENUM'}
                </button>
              ))}
            </div>
          </div>

          {/* SAME_ENTITY 用: Table or Column */}
          {edgeType === 'SAME_ENTITY' && (
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                対象レベル
              </label>
              <div className="flex gap-2">
                {(['Table', 'Column'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => {
                      setNodeType(t);
                    }}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      nodeType === t
                        ? 'border-[var(--color-accent-primary)] bg-[var(--color-accent-primary)]/10 text-[var(--color-accent-primary)]'
                        : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-hover)]'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ソースカラム選択 (REFERENCES / USES_ENUM / column-level SAME_ENTITY) */}
          {(edgeType === 'REFERENCES' ||
            edgeType === 'USES_ENUM' ||
            (edgeType === 'SAME_ENTITY' && nodeType === 'Column')) && (
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                ソースカラム ({parentQN})
              </label>
              <select
                value={srcCol}
                onChange={(e) => {
                  setSrcCol(e.target.value);
                }}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-focus)]"
              >
                <option value="">-- カラム選択 --</option>
                {sourceColumns.map((c) => (
                  <option key={c.qualifiedName} value={c.name}>
                    {c.name} ({c.dataType})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* USES_ENUM: enum 名入力 */}
          {edgeType === 'USES_ENUM' && (
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                Enum 名
              </label>
              <input
                type="text"
                value={enumName}
                onChange={(e) => {
                  setEnumName(e.target.value);
                }}
                placeholder="例: MallCouponType"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm font-mono text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-focus)]"
              />
              <p className="text-xs text-[var(--color-text-muted)] mt-1">
                ※ 既存の Enum 名のみ。新規 Enum は別途 upsert API で先に作成してください
              </p>
            </div>
          )}

          {/* ターゲット: REFERENCES / SAME_ENTITY のみ */}
          {(edgeType === 'REFERENCES' || edgeType === 'SAME_ENTITY') && (
            <>
              <div>
                <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                  ターゲット DB (schema)
                </label>
                <select
                  value={tgtSchema}
                  onChange={(e) => {
                    setTgtSchema(e.target.value);
                    setTgtTable('');
                    setTgtCol('id');
                  }}
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-focus)]"
                >
                  <option value="">-- DB 選択 --</option>
                  {schemas.data?.map((s) => (
                    <option key={s.name} value={s.name}>
                      {s.name} ({s.dbType})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                  ターゲット Table
                </label>
                <select
                  value={tgtTable}
                  onChange={(e) => {
                    setTgtTable(e.target.value);
                    setTgtCol('id');
                  }}
                  disabled={!tgtSchema}
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm font-mono text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-focus)] disabled:opacity-50"
                >
                  <option value="">-- Table 選択 --</option>
                  {tables.data?.tables.map((t) => (
                    <option key={t.qualifiedName} value={t.name}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              {(edgeType === 'REFERENCES' ||
                (edgeType === 'SAME_ENTITY' && nodeType === 'Column')) && (
                <div>
                  <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                    ターゲット Column (デフォルト id)
                  </label>
                  <select
                    value={tgtCol}
                    onChange={(e) => {
                      setTgtCol(e.target.value);
                    }}
                    disabled={!tgtTable}
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm font-mono text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-focus)] disabled:opacity-50"
                  >
                    {targetColumns.length === 0 && <option value="id">id</option>}
                    {targetColumns.map((c) => (
                      <option key={c.qualifiedName} value={c.name}>
                        {c.name} ({c.dataType})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          {/* SAME_ENTITY 用: note */}
          {edgeType === 'SAME_ENTITY' && (
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1">
                同一エンティティの説明 (任意)
              </label>
              <input
                type="text"
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                }}
                placeholder="例: arthur_user_id で連携"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-focus)]"
              />
            </div>
          )}

          {addEdge.isError && (
            <p className="text-sm text-[var(--color-error)]">
              {addEdge.error instanceof Error ? addEdge.error.message : '追加に失敗しました'}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--color-border)]">
            <Button variant="ghost" size="sm" onClick={onClose}>
              キャンセル
            </Button>
            <Button variant="primary" size="sm" loading={addEdge.isPending} onClick={handleSubmit}>
              追加
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
